
import { getUberShader } from "./UberShader";
import { getBasicShader } from "./BasicShader";
import { DepthFormat, invertDepthCompare } from "../CommonRenderTargets";
import { getBufferLayout, getPipelineHash, isValidWideLineGeometry } from "../Pipelines";
import { sideToCullMode } from "../compat";
import { initMaterialBindings, MaterialUniformFlags } from "./MaterialUniforms";
import { ToneMapMethod } from "../Renderer";
import { getDepthBiasValues } from "./DepthBiasMap";

/** @import { VertexBuffer } from "../VertexBuffer" */
/** @import { MultiDrawBatch } from "../gpudrivenrender/MultiDrawBatch" */

export class UberPipeline {

  #renderer;
  /** @type {GPUDevice} */
  #device;

  #pipelines = new Map();
  /** @type {GPUBindGroupLayout[]} */
  #activeBindGroupLayout = [];
  /** @type {GPUColorTargetState[]} */
  #activeTargetsList = [];
  /** @type {VertexBuffer} */
  #vb;
  /** @type {GPUBindGroup} */
  #defaultMaterialBindGroup;

  /**
   * @param {Renderer} renderer
   * @param {GPUBindGroup} defaultMaterialBindGroup - Default bind group for materials without textures
   */
  constructor(renderer, defaultMaterialBindGroup) {
    this.#renderer = renderer;
    this.#device = renderer.getDevice();
    this.#vb = this.#renderer.getVB();
    this.#defaultMaterialBindGroup = defaultMaterialBindGroup;
  }

  #createPipeline(key, geometry, material, materialTextureMask, hasInstanceIdMap, useWboit, idOnly = false) {
    const attributes = geometry.attributes;
    const includeVC = !!(attributes.color && material.vertexColors);
    const isUVW = !!attributes.uvw;
    const hasUV = !!attributes.uv || isUVW;
    const hasTextures = hasUV && materialTextureMask !== 0;
    const hasWideLines = material.wideLines && isValidWideLineGeometry(geometry);
    // Skip normals for THREE.BasicMaterial which signals that rendering without lighting is desired (e.g. gizmos).
    const includeNormals = !!attributes.normal && !(material instanceof THREE.MeshBasicMaterial);

    let pipeline;

    const depthCompare = idOnly || material.depthTest ? invertDepthCompare(material.depthFunc || 'less-equal') : 'always';
    const categoryHighlightingEnabled = this.#renderer.isCategoryHighlightingEnabled();
    const categoryHighlightingFallback = this.#renderer.isCategoryHighlightingFallback();

    if (geometry.isLines) {

      let shader = this.#device.createShaderModule({
        label: 'basic shader 3D lines',
        code: getBasicShader(includeVC, hasWideLines, hasInstanceIdMap, categoryHighlightingEnabled, categoryHighlightingFallback)
      });

      const sampleCount = this.#renderer.getMSAASampleCount();
      pipeline = this.#device.createRenderPipeline({
        label: 'uber pipeline 3D lines',
        layout: this.#device.createPipelineLayout({
          bindGroupLayouts: this.#activeBindGroupLayout
        }),
        vertex: {
          module: shader,
          entryPoint: 'vsmain',
          buffers: getBufferLayout(geometry, false, false, includeVC, false, false)
        },
        fragment: {
          module: shader,
          entryPoint: 'psmain',
          targets: this.#activeTargetsList
        },
        primitive: {
          topology: 'line-list',
          cullMode: 'none'
        },
        depthStencil: {
          depthWriteEnabled: useWboit ? false : material.depthWrite,
          depthCompare,
          format: DepthFormat
        },
        multisample: {
          count: sampleCount
        }
      });
    } else {

      let shader;
      if (includeNormals) {

        const { isGamma, isRGBM } = this.#renderer.getEnvMapEncoding();

        // Activate gamma-decoding for input colors if tone-mapping is used
        // (e.g., see inputToLinear() in uber.wgsl)
        const gammaInput = this.#renderer.getTonemapMethod() != ToneMapMethod.None;
        const useIrradianceMap = this.#renderer.getIBL().hasIrradianceMap();

        shader = this.#device.createShaderModule({
          label: 'uber shader triangles (' + key + ')',
          code: getUberShader(material, hasUV, hasTextures, includeVC, isUVW, isGamma, isRGBM, gammaInput,
          hasInstanceIdMap, useIrradianceMap, useWboit, categoryHighlightingEnabled, categoryHighlightingFallback, idOnly)
        });
      } else {
        // Fallback for shapes without normals (e.g. gizmos with BasicMaterial)
        shader = this.#device.createShaderModule({
          label: 'basic shader triangles (' + key + ')',
          code: getBasicShader(includeVC, hasWideLines, hasInstanceIdMap, categoryHighlightingEnabled, categoryHighlightingFallback, hasUV, hasTextures)
        });
      }

      // "depthBias, depthBiasSlopeScale, and depthBiasClamp have no effect on 'point-list', 'line-list', and
      // 'line-strip' primitives, and must be 0." ...from https://www.w3.org/TR/webgpu/#depth-stencil-state
      const { depthBias, depthBiasSlopeScale } = getDepthBiasValues(material);

      const sampleCountTris = this.#renderer.getMSAASampleCount();
      const pipelineDesc = {
        label: 'uber pipeline triangles (' + key + ')',
        layout: this.#device.createPipelineLayout({
          bindGroupLayouts: this.#activeBindGroupLayout
        }),
        vertex: {
          module: shader,
          entryPoint: 'vsmain',
          buffers: getBufferLayout(geometry, includeNormals, hasUV, includeVC, isUVW, hasWideLines)
        },
        fragment: {
          module: shader,
          entryPoint: idOnly ? 'idOnlyPsMain' : 'psmain',
          targets: this.#activeTargetsList
        },
        primitive: {
          topology: 'triangle-list',
          cullMode: sideToCullMode(material.side)
        },

        depthStencil: {
          depthWriteEnabled: idOnly ? true : useWboit ? false : material.depthWrite,
          depthCompare,
          format: DepthFormat,
          depthBias,
          depthBiasSlopeScale
        },
        multisample: {
          count: sampleCountTris
        }
      };
      pipeline = this.#device.createRenderPipeline(pipelineDesc);
    }

    this.#pipelines.set(key, pipeline);

    return pipeline;
  }

  resetPipelines() {
    this.#pipelines.clear();
  }

  /**
   * @param {GPUBindGroupLayout[]} layouts
   * @param {GPUColorTargetState[]} targets
   */
  reset(layouts, targets) {
    this.#activeBindGroupLayout = layouts;
    this.#activeTargetsList = targets;
  }

  #activateMaterialBindings(passEncoder, material) {

    let materialUniformsMask = material.__gpuUniformsMask | 0;

    if (materialUniformsMask === MaterialUniformFlags.NO_UNIFORMS && !material.needsUpdate) {
      // Reset to default placeholder to avoid stale bindGroup from previous textured material
      passEncoder.setBindGroupIfNeeded(2, this.#defaultMaterialBindGroup);
      return 0;
    }

    if (materialUniformsMask === 0 || material.needsUpdate) {
      materialUniformsMask = initMaterialBindings(this.#device, material, this.#renderer.getPlaceholderTexture());
    }

    if (materialUniformsMask === MaterialUniformFlags.NO_UNIFORMS) {
      // Reset to default placeholder to avoid stale bindGroup from previous textured material
      passEncoder.setBindGroupIfNeeded(2, this.#defaultMaterialBindGroup);
      return 0;
    }

    passEncoder.setBindGroupIfNeeded(2, material.__gpuMaterialUniforms.getBindGroup());

    return materialUniformsMask;
  }

  #preparePipeline(passEncoder, geometry, material, hasInstanceIdMap, useWboit, idOnly = false) {
    const materialUniformsMask = this.#activateMaterialBindings(passEncoder, material);
    const materialTextureMask = materialUniformsMask & MaterialUniformFlags.TEXTURE_MASK;

    const attributes = geometry.attributes;
    const includeVC = attributes.color && material.vertexColors;
    const hasUV = attributes.uv;
    const hasTextures = hasUV && materialTextureMask !== 0;
    const hasWideLines = Boolean(material.wideLines) && isValidWideLineGeometry(geometry);
    const key = getPipelineHash(geometry, material, hasUV, includeVC, hasTextures, hasWideLines, hasInstanceIdMap, useWboit, idOnly);

    let pipeline = this.#pipelines.get(key);
    if (!pipeline) {
      pipeline = this.#createPipeline(key, geometry, material, materialTextureMask, hasInstanceIdMap, useWboit, idOnly);
    }

    passEncoder.setPipelineIfNeeded(pipeline);

    return materialTextureMask;
  }

  drawOne(passEncoder, objectIndex, geometry, material, useWboit, idOnly = false) {
    const materialTextureMask = this.#preparePipeline(passEncoder, geometry, material, false, useWboit, idOnly);
    this.#vb.draw(passEncoder, geometry, objectIndex);
    return materialTextureMask;
  }

  /**
   * @param {GPUCommandEncoder} passEncoder
   * @param {MultiDrawCall} multiDrawCall
   * @param {MultiDrawBatch} mdBatch
   */
  drawMulti(passEncoder, multiDrawCall, mdBatch) {
    const geometry = multiDrawCall.firstGeometry;
    const material = multiDrawCall.firstMaterial;
    const hasInstanceIdMap = mdBatch.enableCulling;
    const materialTextureMask = this.#preparePipeline(passEncoder, geometry, material, hasInstanceIdMap, false);

    this.#vb.drawMulti(passEncoder, multiDrawCall, mdBatch);

    return materialTextureMask;
  }
}